import { GET_ORGANIZATIONS } from "@/gql/organization";
import { IOrganization } from "@/types/organization";
import { getSelectedTeam } from "@/utils/common";
import { handleApolloError } from "@/utils/handle-form";
import { useQuery } from "@apollo/client";
import {
  IGetOrganizationsQuery,
  IGetOrganizationsQueryVariables,
} from "@shared/common/types/api-private/generated/graphql";
import { noopFunction } from "@shared/common/utils/helper";
import { useSession } from "next-auth/react";
import { NextRouter, useRouter } from "next/router";
import React, { ReactNode, useContext, useEffect, useState } from "react";

export interface IOrganizationContext {
  initialized: boolean;
  organizations: IOrganization[] | null;
  setOrganizations: (organizations: IOrganization[]) => void;
  currentOrganization: IOrganization | null;
  isLoading: boolean;
  organizationNotfound: boolean;
  reload: () => void;
}

const context = React.createContext<IOrganizationContext>({
  initialized: false,
  organizations: null,
  setOrganizations: noopFunction,
  currentOrganization: null,
  isLoading: true,
  organizationNotfound: false,
  reload: noopFunction,
});

export function useOrganizationContext() {
  const result = useContext(context);
  if (!result.initialized) {
    throw new Error(
      "Organization context must be used within a OrganizationProvider!"
    );
  }
  return result;
}

interface IOrganizationProviderProps {
  children: ReactNode;
}

const getSlug = (router: NextRouter) => {
  if (router.pathname.includes("[org_slug]")) {
    const slug = router.query.org_slug;
    if (slug && !Array.isArray(slug)) {
      return slug;
    }
  }
  return null;
};

const OrganizationProvider: React.FC<IOrganizationProviderProps> = ({
  children,
}) => {
  const router = useRouter();
  const { data: session, status } = useSession();
  const loading = status === "loading";
  const [organizations, setOrganizations] = useState<IOrganization[]>([]);
  const [currentOrganization, setCurrentOrganization] =
    useState<IOrganization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [organizationNotfound, setOrganizationNotfound] = useState(false);

  useEffect(() => {
    const organizationId = getSlug(router);

    if (organizationId) {
      setCurrentOrganization(
        organizations.find((val) => val.slug === organizationId) ?? null
      );
      return;
    }

    setCurrentOrganization(
      organizations.find(
        (organization) => organization.id === getSelectedTeam()
      ) ??
        organizations[0] ??
        null
    );
  }, [organizations, router]);

  const { refetch, loading: orgLoading } = useQuery<
    IGetOrganizationsQuery,
    IGetOrganizationsQueryVariables
  >(GET_ORGANIZATIONS, {
    skip: loading || !session,
    onCompleted: (data) => {
      // TODO: remove "as"
      setOrganizations(data.organizations as IOrganization[]);
      setIsLoading(false);

      if (
        getSlug(router) &&
        // TODO: remove optional
        !data.organizations.find((val) => val?.slug === getSlug(router))
      ) {
        setOrganizationNotfound(true);
      }
    },
    onError: handleApolloError,
  });

  return (
    <context.Provider
      value={{
        initialized: true,
        organizations,
        currentOrganization,
        isLoading: loading || isLoading || orgLoading,
        setOrganizations,
        organizationNotfound,
        reload: refetch,
      }}
    >
      {children}
    </context.Provider>
  );
};

export default OrganizationProvider;
